const MAX_RETRIES = 4;

export default class RtcRingtonePlayer {
  constructor({ source, AudioCtor, onEvent = () => {}, onStateChange = () => {} }) {
    this.source = source;
    this.AudioCtor = AudioCtor;
    this.onEvent = onEvent;
    this.onStateChange = onStateChange;
    this.audio = null;
    this.isRinging = false;
    this.generation = 0;
    this.retryCount = 0;
    this.retryTimer = null;
    this.playPending = false;
    Object.preventExtensions(this);
  }

  init() {
    if (this.audio || !this.AudioCtor || !this.source) return this.audio;
    const audio = new this.AudioCtor();
    audio.preload = 'auto';
    audio.volume = 1;
    audio.muted = false;
    audio.loop = true;
    audio.onloadeddata = () => {
      this.onEvent({ event: 'ringtone-resource-ready', readyState: audio.readyState });
      this.tryPlay();
    };
    audio.oncanplay = () => {
      this.onEvent({ event: 'ringtone-can-play', readyState: audio.readyState });
      this.tryPlay();
    };
    audio.onerror = () => {
      this.onEvent({ event: 'ringtone-resource-failed', readyState: audio.readyState });
      this.scheduleRetry();
    };
    audio.src = this.source;
    this.audio = audio;
    if (typeof audio.load === 'function') audio.load();
    return audio;
  }

  setRinging(value) {
    if (this.isRinging === value) return;
    this.isRinging = value;
    this.onStateChange(value);
  }

  start() {
    const audio = this.init();
    if (!audio) {
      this.onEvent({ event: 'ringtone-play-failed', errorName: 'AudioUnavailable' });
      return;
    }
    if (this.isRinging) return;
    this.retryCount = 0;
    this.playPending = false;
    this.generation++;
    this.setRinging(true);
    this.onEvent({ event: 'ringtone-play-requested', readyState: audio.readyState, muted: Boolean(audio.muted), volume: audio.volume });
    this.tryPlay();
  }

  retry() {
    if (!this.isRinging) {
      this.start();
      return;
    }
    this.tryPlay();
  }

  stop() {
    const wasRinging = this.isRinging;
    this.setRinging(false);
    this.generation++;
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.retryCount = 0;
    this.playPending = false;
    if (this.audio) {
      this.audio.pause();
      try { this.audio.currentTime = 0; } catch (_) { /* metadata may not be ready */ }
    }
    if (wasRinging) this.onEvent({ event: 'ringtone-stopped' });
  }

  scheduleRetry() {
    const audio = this.audio;
    if (!this.isRinging || !audio || this.playPending || this.retryTimer) return;
    if (!audio.paused && !audio.ended) return;
    if (this.retryCount >= MAX_RETRIES) {
      this.setRinging(false);
      this.onEvent({ event: 'ringtone-play-failed', errorName: 'AudioResourceUnavailable', readyState: audio.readyState, attempts: this.retryCount });
      return;
    }
    const attempt = ++this.retryCount;
    const delay = Math.min(100 * (2 ** (attempt - 1)), 800);
    this.onEvent({ event: 'ringtone-load-retry', readyState: audio.readyState, attempt, delayMs: delay });
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.isRinging || audio !== this.audio) return;
      if (audio.readyState < 3 && typeof audio.load === 'function') audio.load();
      this.tryPlay();
    }, delay);
  }

  tryPlay() {
    const audio = this.audio;
    if (!this.isRinging || !audio || this.playPending) return;
    if (audio.readyState < 3) {
      this.onEvent({ event: 'ringtone-waiting-for-resource', readyState: audio.readyState });
      this.scheduleRetry();
      return;
    }
    if (!audio.paused && !audio.ended) return;
    const generation = this.generation;
    this.playPending = true;
    this.onEvent({ event: 'ringtone-play-ready', readyState: audio.readyState });
    try { audio.currentTime = 0; } catch (_) { /* metadata may not be ready */ }

    const onStarted = () => {
      this.playPending = false;
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
      if (!this.isRinging || generation !== this.generation) {
        audio.pause();
        return;
      }
      this.retryCount = 0;
      this.onEvent({ event: 'ringtone-play-started', readyState: audio.readyState, paused: Boolean(audio.paused) });
    };
    const onFailed = error => {
      this.playPending = false;
      if (!this.isRinging || generation !== this.generation) return;
      this.onEvent({
        event: 'ringtone-play-failed',
        errorName: error && error.name || 'PlaybackError',
        errorMessage: error && error.message || '',
        readyState: audio.readyState
      });
      this.scheduleRetry();
    };

    try {
      const playback = audio.play();
      if (playback && typeof playback.then === 'function') playback.then(onStarted, onFailed);
      else onStarted();
    } catch (error) {
      onFailed(error);
    }
  }

  destroy() {
    this.stop();
    if (!this.audio) return;
    this.audio.onloadeddata = null;
    this.audio.oncanplay = null;
    this.audio.onerror = null;
    this.audio.src = '';
    this.audio = null;
  }
}
